import * as React from "react"
import { cn } from "@/lib/utils"

function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md bg-white/10",
        "before:absolute before:inset-0",
        "before:-translate-x-full before:animate-shimmer",
        "before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent",
        className
      )}
      {...props}
    />
  )
}

function SkeletonCard() {
  return (
    <div className="space-y-3 p-4">
      <Skeleton className="h-12 w-12 rounded-lg" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-[250px]" />
        <Skeleton className="h-4 w-[200px]" />
      </div>
    </div>
  )
}

function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      <Skeleton className="h-10 w-full" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full" />
      ))}
    </div>
  )
}

function SkeletonDashboard() {
  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
      {/* Charts Row */}
      <div className="grid gap-4 md:grid-cols-2">
        <Skeleton className="h-[400px]" />
        <Skeleton className="h-[400px]" />
      </div>
      {/* Table */}
      <SkeletonTable />
    </div>
  )
}

function SkeletonMetricGrid({ cols = 4, rows = 1 }: { cols?: number; rows?: number }) {
  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
    6: 'md:grid-cols-3 lg:grid-cols-6',
  }[cols] || 'md:grid-cols-2 lg:grid-cols-4';

  return (
    <div className="space-y-6">
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div key={rowIdx} className={`grid gap-4 ${gridCols}`}>
          {Array.from({ length: cols }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ))}
    </div>
  );
}

function SkeletonChart({ height = 400 }: { height?: number }) {
  return (
    <div className="space-y-3">
      <Skeleton className="h-6 w-48" />
      <Skeleton className="w-full" style={{ height: `${height}px` }} />
      <div className="flex gap-4 justify-center">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-24" />
      </div>
    </div>
  );
}

function SkeletonChartGrid({ charts = 2, height = 400 }: { charts?: number; height?: number }) {
  const gridCols = charts === 1 ? '' : charts === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';
  
  return (
    <div className={`grid gap-6 ${gridCols}`}>
      {Array.from({ length: charts }).map((_, i) => (
        <SkeletonChart key={i} height={height} />
      ))}
    </div>
  );
}

function SkeletonTabs({ tabs = 5 }: { tabs?: number }) {
  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b pb-2">
        {Array.from({ length: tabs }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-32" />
        ))}
      </div>
      <div className="space-y-4">
        <SkeletonMetricGrid cols={4} />
        <SkeletonTable rows={8} />
      </div>
    </div>
  );
}

export { 
  Skeleton, 
  SkeletonCard, 
  SkeletonTable, 
  SkeletonDashboard,
  SkeletonMetricGrid,
  SkeletonChart,
  SkeletonChartGrid,
  SkeletonTabs
}

